import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Users, Video, MessageSquare, Star, Calendar, Sparkles, Heart, Crown, Palmtree, Quote, Play, ExternalLink } from 'lucide-react';
import { BRAND_IMAGES } from '@/constants/brandImages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Client testimonials
const TESTIMONIALS = [
  {
    name: "Dr. Ursula Whitehead",
    title: "Leadership Coach",
    quote: "I am really grateful for the opportunity to work with Dr. Nicole and Crystal. They were just personal, inspiring and life-changing. If you're interested in making a change in your life and going to the next level, become a part of the Sister Diamond Collective. You will not regret it.",
    image: "https://i.ontraport.com/260712.2334b2d50dcbe67a0fd0db7cf0ad7e13.PNG",
    vimeoUrl: "https://vimeo.com/739435943/c723b6f9ed"
  },
  {
    name: "Tracy Higgs",
    title: "Director, Office of Investigations, Nuclear Regulatory Commission",
    quote: "The coaching I received transformed how I show up as a leader. I learned to lead with both strength and softness.",
    image: "https://i.vimeocdn.com/video/1488692012-638ac0591ff0d31c43673736ff881dbfaffb1d81d765a9895170bae0f4516f4e-d",
    videoId: "849606583"
  },
  {
    name: "CeWyon Chandler-Ward",
    title: "Serial Entrepreneur | Angel Investor",
    quote: "Sister Diamonds gave me the space to breathe, reflect, and strategize. The community is unlike anything I've experienced.",
    image: "https://i.vimeocdn.com/video/1489342970-59f6bd1640a0844f1cf844c0c52e8858da1c3718a81d1c7336311708bc3cabd5-d",
    videoId: "849760143"
  },
  {
    name: "Arlene Henderson",
    title: "Business Consultant, Executive Coach",
    quote: "Working with Crystal and Dr. Nicole helped me unlock a level of clarity and confidence I didn't know was possible.",
    image: "https://i.vimeocdn.com/video/1488709445-f620e9970488ed897bc868536f3e858af8a4498522a0d8a6d2717817580421ef-d",
    videoId: "849614055"
  }
];

const LandingPage = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coaches`);
      // Prioritize Black women coaches on the home display
      const allCoaches = response.data;
      const blackWomenCoaches = allCoaches.filter(c => c.is_black_female);
      const otherCoaches = allCoaches.filter(c => !c.is_black_female);
      const sortedCoaches = [...blackWomenCoaches, ...otherCoaches];
      setCoaches(sortedCoaches.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const pathways = [
    {
      name: "Inner Circle",
      price: "$97/mo",
      description: "Your sanctuary for rest and sisterhood",
      icon: Heart,
      color: "bg-terracotta",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=300&fit=crop&q=80"
    },
    {
      name: "Inner Edge",
      price: "$5,000",
      description: "Deep dive into your power and presence",
      icon: Sparkles,
      color: "bg-forest",
      image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=400&h=300&fit=crop&q=80"
    },
    {
      name: "Signature Coaching",
      price: "$8,000",
      description: "Personalized guidance for your becoming",
      icon: Users,
      color: "bg-terracotta",
      image: "https://images.unsplash.com/photo-1573496267478-37727ee5b694?w=400&h=300&fit=crop&q=80"
    },
    {
      name: "Sister Diamonds Collective (SDC)",
      price: "$25,000",
      description: "VIP transformation container with unlimited coaching, retreats, and exclusive sisterhood",
      icon: Crown,
      color: "bg-forest",
      image: "https://customer-assets.emergentagent.com/job_7ae042af-d5fd-4d89-854a-3356c8c74821/artifacts/utah8q1b_SDC6.jpg"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section relative overflow-hidden">
        <Navbar minimal />
        
        {/* Background Image of Black Women */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src={BRAND_IMAGES.sisterhood.ghanaJoy} 
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              {/* Logo moved here */}
              <Link to="/" className="inline-block mb-6">
                <span className="font-heading text-2xl font-bold text-white">
                  Sister Diamonds
                </span>
              </Link>
              <span className="inline-block px-4 py-2 bg-white/10 text-white/90 rounded-full text-sm font-medium mb-6 ml-4">
                Collective
              </span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
                You Don't Have to <span className="text-terracotta">Carry It</span> Alone
              </h1>
              <p className="text-xl text-white/80 mb-8 max-w-lg leading-relaxed">
                A sacred sisterhood for high-capacity Black women ready to exhale, 
                be seen, and step into their wholeness — without sacrificing their peace.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/costa-rica-retreat">
                  <Button size="lg" className="bg-terracotta text-white hover:bg-terracotta/90 h-14 px-8 text-lg" data-testid="hero-retreat-btn">
                    <Palmtree className="mr-2 h-5 w-5" /> Book Retreat
                  </Button>
                </Link>
                <Link to="/inner-circle">
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-14 px-8 text-lg" data-testid="hero-join-btn">
                    Join Inner Circle <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#learn-more">
                  <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 h-14 px-6 text-lg" data-testid="hero-learn-more-btn">
                    Learn More
                  </Button>
                </a>
              </div>
              <div className="mt-6">
                <Link to="/login" className="text-white/80 hover:text-white text-sm underline underline-offset-4 transition-colors" data-testid="hero-member-login">
                  Already a member? Log in here
                </Link>
              </div>
            </div>

            {/* Hero Stats with Sisterhood Image */}
            <div className="hidden lg:block animate-fade-in-delay-2">
              <div className="relative">
                {/* Sisterhood Image */}
                <div className="mb-6 rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src={BRAND_IMAGES.sisterhood.laughing} 
                    alt="Black women in sisterhood, laughing and connecting" 
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="text-center p-6 bg-white/5 rounded-xl">
                      <p className="text-4xl font-bold text-white mb-2">Diverse</p>
                      <p className="text-white/70">Seasoned Certified Coaches</p>
                    </div>
                    <div className="text-center p-6 bg-white/5 rounded-xl">
                      <p className="text-5xl font-bold text-white mb-2">500+</p>
                      <p className="text-white/70">Brilliant Sisters</p>
                    </div>
                    <div className="text-center p-6 bg-white/5 rounded-xl">
                      <p className="text-5xl font-bold text-white mb-2">5</p>
                      <p className="text-white/70">Paths to Wholeness</p>
                    </div>
                    <div className="text-center p-6 bg-white/5 rounded-xl">
                      <p className="text-5xl font-bold text-white mb-2">∞</p>
                      <p className="text-white/70">Permission to Rest</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Inner Circle Value Section */}
      <section id="learn-more" className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Your Sanctuary Awaits</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2">
              Inner Circle Membership
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">
              This is where restoration begins. A sacred space designed for brilliant Black women 
              ready to exhale, reclaim their peace, and soar.
            </p>
          </div>

          {/* Group photo of mature Black women in colorful attire - mirrors community */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-lg">
            <img 
              src={BRAND_IMAGES.sisterhood.communityJoy} 
              alt="Mature Black women friends laughing together in colorful attire - sisterhood community" 
              className="w-full h-80 md:h-[400px] lg:h-[450px] object-cover object-[center_20%]"
            />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {[
              { icon: MessageSquare, title: "Weekly Soul Food", description: "Audio or text wisdom to nourish your spirit" },
              { icon: Sparkles, title: "Reflection Prompts", description: "Questions that invite you back to yourself" },
              { icon: Video, title: "Monthly Gatherings", description: "Live circles with Crystal, Nicole, and your sisters" },
              { icon: Users, title: "Sacred Sisterhood", description: "Communities where you can speak what's sacred" },
              { icon: Star, title: "The Vault", description: "Soul-centered content for your becoming" },
              { icon: Calendar, title: "First Invitations", description: "Priority access to retreats and special gatherings" },
            ].map((feature, index) => (
              <Card key={index} className="card-feature group" data-testid={`ic-feature-${index}`}>
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-xl bg-terracotta/10 flex items-center justify-center mb-4 group-hover:bg-terracotta group-hover:text-white transition-colors">
                    <feature.icon className="h-6 w-6 text-terracotta group-hover:text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-forest mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Link to="/inner-circle">
              <Button size="lg" className="btn-secondary h-14 px-10 text-lg">
                Say Yes to Yourself - $97/month <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pathways Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Go Deeper</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2">
              Choose Your Path to Wholeness
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Ready to go deeper? Each pathway is designed for a different season of your becoming.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pathways.map((pathway, index) => (
              <Card key={index} className="card-base hover:shadow-xl transition-all group overflow-hidden" data-testid={`pathway-${index}`}>
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={pathway.image} 
                    alt={pathway.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className={`absolute bottom-3 left-3 h-10 w-10 rounded-xl ${pathway.color} flex items-center justify-center`}>
                    <pathway.icon className="h-5 w-5 text-white" />
                  </div>
                </div>
                <CardContent className="p-5 text-center">
                  <h3 className="font-heading text-lg font-semibold text-forest mb-1">{pathway.name}</h3>
                  <p className="text-terracotta font-bold text-xl mb-2">{pathway.price}</p>
                  <p className="text-gray-600 text-sm line-clamp-2">{pathway.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/pathways">
              <Button className="btn-primary">Discover Your Path</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* YUMMY Methodology Section */}
      <section className="py-24 bg-gradient-to-br from-cream via-white to-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Our Proprietary Framework</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2 mb-4">
                The YUMMY Methodology™
              </h2>
              <p className="text-xl text-terracotta font-heading font-semibold mb-6">
                <span className="text-forest">Y</span>our <span className="text-forest">U</span>ltimate <span className="text-forest">M</span>ost <span className="text-forest">M</span>eaningful <span className="text-forest">Y</span>et
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                There are a lot of good things you can do — but <strong className="text-terracotta">what is your YUMMY move?</strong>
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Sister Diamonds isn't just coaching — it's a complete transformation system built on our proprietary <strong>YUMMY Methodology™</strong>. 
                This holistic framework integrates coaching, community, and individual accountability to help you identify and pursue your most meaningful next step.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">Y</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest">Your Truth</h4>
                    <p className="text-gray-600">Discover and embrace your authentic self without apology</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-forest flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">U</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest">Unwavering Support</h4>
                    <p className="text-gray-600">Community that holds you accountable to your highest self</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">M</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest">Mindful Transformation</h4>
                    <p className="text-gray-600">Strategic coaching that honors your pace and process</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-forest flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">M</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest">Meaningful Action</h4>
                    <p className="text-gray-600">Individual accountability that moves you from intention to impact</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-terracotta flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">Y</span>
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-forest">Your Legacy</h4>
                    <p className="text-gray-600">Stepping into the version of yourself that changes everything</p>
                  </div>
                </div>
              </div>

              <p className="text-gray-600 italic border-l-4 border-terracotta pl-4">
                "What makes us different? We help you cut through the noise to find your YUMMY move — that next step that's not just good, but truly meaningful for your journey."
              </p>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="relative">
                <img 
                  src={BRAND_IMAGES.sisterDiamonds.beachGroup}
                  alt="Sister Diamonds community on beach retreat"
                  className="rounded-2xl shadow-2xl w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-forest text-white p-6 rounded-xl shadow-lg max-w-xs hidden md:block">
                  <p className="font-heading text-lg font-semibold mb-2">What's Your YUMMY Move?</p>
                  <p className="text-sm text-white/80">Your Ultimate Most Meaningful Yet — the next step that changes everything</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Coach Preview Section */}
      <section className="py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Your Guides</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2">
              Meet Crystal &amp; Dr. Nicole
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              The visionary co-founders who created this sanctuary. Experience their unique "Wonder Twin" approach to soul-centered coaching.
            </p>
          </div>

          {/* Founders Card */}
          <div className="max-w-3xl mx-auto mb-16">
            <Card className="card-base overflow-hidden">
              <div className="grid md:grid-cols-2">
                {/* Image first on mobile, second on desktop */}
                <div className="relative h-64 sm:h-80 md:h-auto md:order-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_volition-membership/artifacts/wdy6rj8w_crystal%20and%20drnicole.png" 
                    alt="Crystal &amp; Dr. Nicole"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="bg-forest p-8 text-white md:order-1">
                  <span className="text-terracotta text-sm font-medium">Co-Founders &amp; Wonder Twin Guides</span>
                  <h3 className="font-heading text-3xl font-bold mt-2 mb-4">Crystal &amp; Dr. Nicole</h3>
                  <p className="text-white/80 mb-6">
                    Otherwise known as the Wonder-Twins. When you work with them together, you get the best of both worlds — 
                    strategic wisdom and deep soul work. They see you, and they won&apos;t let you shrink.
                  </p>
                  <Link to="/coaches/founder-crystal-nicole">
                    <Button className="bg-white text-forest hover:bg-white/90">
                      Meet Your Guides <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </div>

          {/* Why Coaching Matters Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <Card className="card-base bg-gradient-to-r from-forest/5 to-terracotta/5 border-none">
              <CardContent className="p-8 text-center">
                <Quote className="h-10 w-10 text-terracotta mx-auto mb-4" />
                <p className="font-heading text-xl text-forest mb-4 leading-relaxed">
                  "Behind every successful person is a team of coaches, mentors, and sponsors who saw what they couldn't see in themselves."
                </p>
                <p className="text-gray-600 mb-6">
                  <strong>Oprah Winfrey</strong> credits her mentor Maya Angelou. <strong>Serena Williams</strong> has worked with multiple coaches throughout her career. 
                  <strong> Michelle Obama</strong> speaks of having coaches for different seasons of life. You deserve that same level of support.
                </p>
                <div className="bg-white/80 rounded-xl p-6">
                  <h4 className="font-heading text-lg text-forest mb-3">The most successful women don't do it alone.</h4>
                  <p className="text-gray-600 text-sm">
                    They surround themselves with guides who bring diverse perspectives, specialized expertise, the ability to see their blind spots, and hold them accountable to their highest and best selves. 
                    Your next breakthrough might come from a voice you didn't expect.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Our Coaching Philosophy */}
          <div className="text-center mb-12">
            <h3 className="font-heading text-2xl font-semibold text-forest mb-4">A Community of Diverse, Seasoned and Certified Coaches</h3>
            <p className="text-gray-600 max-w-3xl mx-auto mb-6">
              We believe in <strong>Ubuntu</strong> — "I am because we are." Our coaches come from different backgrounds, 
              bringing the experiences, credentials, and expertise that matter for your specific journey. 
              Crystal &amp; Dr. Nicole personally vet each guide to ensure they embody our values of wholeness, 
              authenticity, and soul-centered transformation.
            </p>
            <p className="text-gray-500 text-sm max-w-2xl mx-auto italic">
              Whether your breakthrough comes from shared experience or fresh perspective, 
              you'll have an abundance of guides to choose from — each one here because they're right for our tribe.
            </p>
          </div>

          {/* Coach Preview Grid */}
          <div className="text-center mb-8">
            <p className="text-gray-600">Full access with Signature Coaching or SDC membership</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-20 w-20 mx-auto rounded-full bg-white animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap justify-center gap-4">
              {coaches.map((coach) => (
                <div key={coach.id} className="text-center group" data-testid={`coach-preview-${coach.id}`}>
                  <div className="h-20 w-20 rounded-full overflow-hidden border-3 border-white shadow-md mx-auto mb-2 group-hover:border-terracotta transition-colors">
                    <img 
                      src={coach.image_url} 
                      alt={coach.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm font-medium text-forest">{coach.name}</p>
                </div>
              ))}
              <Link to="/coaches" className="flex items-center justify-center">
                <div className="h-20 w-20 rounded-full bg-forest text-white flex items-center justify-center font-bold hover:bg-terracotta transition-colors text-sm">
                  See All
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Costa Rica Retreat Section - Featured Banner */}
      <section className="py-24 bg-forest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <Badge className="bg-terracotta text-white mb-4 text-sm px-4 py-1">
                <Palmtree className="inline h-4 w-4 mr-2" />
                June 26 – July 1, 2026
              </Badge>
              <h2 className="font-heading text-4xl md:text-5xl font-bold mb-4">
                Just Be: Soft Power™ Retreat
              </h2>
              <p className="text-xl text-white/90 mb-6 leading-relaxed">
                Costa Rica awaits. A soul-deep, luxury retreat for high-capacity women ready to release, restore, and rise — in their softness, not their striving.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Return home rested, restored, and clear on your next chapter',
                  'Release the weight you have been carrying in your body and spirit',
                  'Build lifelong bonds with women who truly see you',
                  'Reconnect with the woman you were before the world told you who to be',
                  'Step back into your life with renewed clarity and soft power'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <Star className="h-5 w-5 text-terracotta flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-2xl font-heading font-bold text-terracotta mb-6">
                Starting at $4,997
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/costa-rica-retreat">
                  <Button size="lg" className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="retreat-learn-more-btn">
                    View Full Details <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://calendly.com/crystalanddrnicole/justbesoftpowerretreatinterest" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="retreat-call-btn">
                    <Calendar className="mr-2 h-5 w-5" /> Schedule Interest Call
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_df51b24f-e90b-4292-a1cc-f4514c1e9aaa/artifacts/ht6uqqxr_2%20%281%29.png" 
                alt="Woman in peaceful retreat setting"
                className="rounded-2xl w-full max-w-lg h-auto object-cover shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-terracotta font-medium text-sm uppercase tracking-wide">Sister Stories</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-forest mt-2">
              Voices From the Sisterhood
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Real transformation stories from brilliant women who found their way home.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card key={index} className="card-base overflow-hidden group hover:shadow-xl transition-shadow" data-testid={`testimonial-${index}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Image/Video Thumbnail */}
                    <div className="relative w-full md:w-1/3 h-48 md:h-auto flex-shrink-0">
                      <img 
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                      {(testimonial.videoId || testimonial.vimeoUrl) && (
                        <a 
                          href={testimonial.vimeoUrl || `https://vimeo.com/${testimonial.videoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors"
                        >
                          <div className="h-14 w-14 rounded-full bg-terracotta flex items-center justify-center">
                            <Play className="h-6 w-6 text-white ml-1" />
                          </div>
                        </a>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-6 flex-1">
                      <Quote className="h-8 w-8 text-terracotta/30 mb-3" />
                      <p className="text-gray-700 mb-4 leading-relaxed line-clamp-4">
                        &quot;{testimonial.quote}&quot;
                      </p>
                      <div>
                        <p className="font-heading font-semibold text-forest">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.title}</p>
                      </div>
                      {(testimonial.videoId || testimonial.vimeoUrl) && (
                        <a 
                          href={testimonial.vimeoUrl || `https://vimeo.com/${testimonial.videoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-terracotta text-sm font-medium mt-3 hover:underline"
                        >
                          <Play className="h-4 w-4" /> Watch Video
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 hero-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            You're Allowed to Rest, Sis
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Join hundreds of brilliant Black women who stopped carrying the world alone 
            and stepped into a sisterhood that sees them, holds them, and celebrates them.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://calendly.com/crystalanddrnicole/justbesoftpowerretreatinterest" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-terracotta text-white hover:bg-terracotta/90 h-14 px-8 text-lg" data-testid="cta-retreat-call-btn">
                <Calendar className="mr-2 h-5 w-5" /> Book a Soul Connection Call
              </Button>
            </a>
            <Link to="/inner-circle">
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 h-14 px-8 text-lg" data-testid="cta-join-btn">
                Say Yes to Yourself <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
